import React from 'react';
import { motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';

export interface CardProps {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  glass?: boolean;
  onClick?: () => void;
}

const Card: React.FC<CardProps> = ({ 
  children, 
  className, 
  hover = false, 
  glass = false,
  onClick 
}) => {
  const baseClasses = 'rounded-xl transition-all duration-300';
  
  const glassClasses = glass 
    ? 'bg-white/10 backdrop-blur-md border border-white/20 shadow-2xl' 
    : 'bg-white border border-gray-200 shadow-sm';
  
  const hoverClasses = hover 
    ? 'hover:shadow-xl hover:border-gray-300 cursor-pointer transform hover:-translate-y-1' 
    : '';

  const classes = twMerge(baseClasses, glassClasses, hoverClasses, className);

  const MotionComponent = onClick ? motion.div : motion.div;

  return (
    <MotionComponent
      className={classes}
      onClick={onClick}
      whileHover={hover ? { scale: 1.02 } : {}}
      whileTap={hover ? { scale: 0.98 } : {}}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </MotionComponent>
  );
};

export interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const CardHeader: React.FC<CardHeaderProps> = ({ children, className }) => (
  <div className={twMerge('px-6 py-4 border-b border-gray-100', className)}>
    {children}
  </div>
);

export interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export const CardContent: React.FC<CardContentProps> = ({ children, className }) => (
  <div className={twMerge('px-6 py-4', className)}>
    {children}
  </div>
);

export interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const CardFooter: React.FC<CardFooterProps> = ({ children, className }) => (
  <div className={twMerge('px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl', className)}>
    {children}
  </div>
);

export default Card;
